import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { authService, GoogleLoginResponse } from '../services/auth.service';
import { setAccessToken } from '../services/api';

const API_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3001/api'
  : process.env.REACT_APP_API_URL || '/api';

interface ImpersonatedUser {
  id: number;
  email: string;
  role: string;
  name: string;
}

interface AuthContextType {
  user: any | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: (idToken: string) => Promise<GoogleLoginResponse>;
  logout: () => Promise<void>;
  setAuthFromResponse: (response: { access_token: string; user?: any }) => Promise<void>;
  impersonate: (userId: number) => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const parseJwtPayload = (token: string): any => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return {};
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);

  // 페이지 로드 시 Refresh Token 쿠키로 Access Token 복원
  useEffect(() => {
    axios
      .post(`${API_URL}/auth/refresh`, {}, { withCredentials: true })
      .then(({ data }) => {
        setAccessToken(data.access_token);
        setToken(data.access_token);
        setUser(data.user);
      })
      .catch(() => {
        setAccessToken(null);
        setToken(null);
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  // IPT-447: 응답 body의 user를 신뢰하지 않고 서버 profile에서 권위있는 role 조회
  // 공격자가 로그인 응답 body의 role을 변조해도 DB 기반 실제 role을 사용하도록 보장
  const applyAuthSecure = async (access_token: string) => {
    setAccessToken(access_token);
    const { data } = await axios.get(`${API_URL}/auth/profile`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    setToken(access_token);
    setUser(data);
  };

  const login = async (email: string, password: string) => {
    const response = await authService.login({ email, password });
    await applyAuthSecure(response.access_token);
  };

  const googleLogin = async (idToken: string): Promise<GoogleLoginResponse> => {
    const response = await authService.googleLogin(idToken);
    if (response.access_token) {
      await applyAuthSecure(response.access_token);
    }
    return response;
  };

  const setAuthFromResponse = async (response: { access_token: string; user?: any }) => {
    await applyAuthSecure(response.access_token);
  };

  // IPT-488: Admin이 특정 유저로 임시 전환
  const impersonate = async (userId: number) => {
    const currentToken = token;
    const { data } = await axios.post(
      `${API_URL}/users/${userId}/impersonate`,
      {},
      { headers: { Authorization: `Bearer ${currentToken}` } },
    );
    // 원본 admin 토큰은 저장하지 않는다.
    // localStorage 에 두면 XSS 한 번에 관리자 토큰이 통째로 탈취되고,
    // 실제로 이 값을 다시 읽는 코드도 없었다 (api.ts 의 메모리 보관 원칙과도 어긋남).
    // 임시 토큰 적용 (profile 조회 없이 서버가 반환한 impersonatedUser 사용)
    setAccessToken(data.accessToken);
    setToken(data.accessToken);
    // impersonate 대상 유저 정보를 서버 응답에서 직접 설정
    setImpersonatedUser(data.impersonatedUser);
    // user 상태는 impersonatedUser 기반으로 설정 (권한 적용)
    setUser({
      id: data.impersonatedUser.id,
      email: data.impersonatedUser.email,
      role: data.impersonatedUser.role,
      name: data.impersonatedUser.name,
    });
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      // silent
    } finally {
      setAccessToken(null);
      setToken(null);
      setUser(null);
      setImpersonatedUser(null);
      // 과거 버전이 남겼을 수 있는 값을 정리한다 (신규 저장은 하지 않는다)
      localStorage.removeItem('adminToken');
    }
  };

  const isImpersonating = token ? !!parseJwtPayload(token).impersonatedUserId : false;

  return (
    <AuthContext.Provider
      value={{
        user, token, login, googleLogin, logout, setAuthFromResponse,
        impersonate, isAuthenticated: !!token, isLoading,
        isImpersonating, impersonatedUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
